/** 3D viewer for a strip footing under a wall. Shows ~3 m of the strip with
 * the wall rising above grade, the footing below, the bottom main mesh and
 * the longitudinal distribution bars. */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds } from '@react-three/drei';
import * as THREE from 'three';

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525, '1/2': 1.27, '5/8': 1.5875, '3/4': 1.905, '1': 2.54, '1 3/8': 3.4925,
};

const COLOR_CONCRETE = '#94A3B8';
const COLOR_WALL = '#A16207';
const COLOR_SOIL = '#7C2D12';
const COLOR_BAR_MAIN = '#F59E0B';   // transverse main bars (across B)
const COLOR_BAR_DIST = '#8B5CF6';   // longitudinal distribution

interface Props {
  B: number;
  Hf: number;
  tw: number;
  df: number;
  cover: number;
  diam_main: string;
  s_main: number;
  diam_dist: string;
  s_dist: number;
  /** Length of the strip shown in the 3D scene (cm). */
  L_show?: number;
}

export function Strip3DView({
  B, Hf, tw, df, cover,
  diam_main, s_main, diam_dist, s_dist,
  L_show = 300,
}: Props) {
  const maxDim = Math.max(L_show, B, df + 60);
  const scale = 5 / Math.max(maxDim, 1);

  const L_s = L_show * scale;
  const B_s = B * scale;
  const Hf_s = Hf * scale;
  const tw_s = tw * scale;
  const df_s = df * scale;

  const d_main = DIAM_INCH_TO_CM[diam_main] ?? 1.27;
  const d_dist = DIAM_INCH_TO_CM[diam_dist] ?? 0.9525;

  // Origin: footing centroid (top of slab at y = Hf, bottom at y = 0)
  const yMainBar = cover + d_main / 2;  // from bottom of slab (cm)
  const yDistBar = cover + d_main + d_dist / 2;
  const wallTopAboveGrade = 60;
  const yWallTop = df + wallTopAboveGrade;

  // Main bars (transverse, across B). Their length = B, oriented along z.
  // We distribute them along x at spacing s_main, centered at origin.
  const mainXs = useMemo(() => {
    const n = Math.max(2, Math.floor(L_show / s_main));
    const start = -L_show / 2 + (L_show - (n - 1) * s_main) / 2;
    return Array.from({ length: n }, (_, i) => start + i * s_main);
  }, [L_show, s_main]);

  // Distribution bars (along x, run parallel to the wall). Distribute across
  // z at spacing s_dist, centered.
  const distZs = useMemo(() => {
    const n = Math.max(2, Math.floor(B / s_dist));
    const start = -B / 2 + (B - (n - 1) * s_dist) / 2;
    return Array.from({ length: n }, (_, i) => start + i * s_dist);
  }, [B, s_dist]);

  const yScene = (h_cm: number) => -Hf_s / 2 + h_cm * scale;

  return (
    <Canvas
      camera={{ position: [6, 5, 7], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
      gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 5]} intensity={0.95} />
      <directionalLight position={[-4, -2, -3]} intensity={0.28} />

      <Grid
        args={[30, 30]} cellSize={0.25} cellThickness={0.4}
        cellColor="#475569" sectionSize={1} sectionThickness={1}
        sectionColor="#334155" fadeDistance={20} fadeStrength={1.2}
        position={[0, yScene(df) - 0.001, 0]} infiniteGrid
      />

      <Bounds fit clip observe margin={1.45}>
        {/* SOIL */}
        <mesh position={[0, yScene(df / 2), 0]}>
          <boxGeometry args={[L_s * 1.4, df_s, B_s * 1.6]} />
          <meshStandardMaterial color={COLOR_SOIL} transparent opacity={0.10} roughness={1.0} />
        </mesh>

        {/* FOOTING SLAB */}
        <mesh position={[0, yScene(Hf / 2), 0]}>
          <boxGeometry args={[L_s, Hf_s, B_s]} />
          <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.42} roughness={0.85} />
        </mesh>
        <lineSegments position={[0, yScene(Hf / 2), 0]}>
          <edgesGeometry args={[new THREE.BoxGeometry(L_s, Hf_s, B_s)]} />
          <lineBasicMaterial color={COLOR_CONCRETE} />
        </lineSegments>

        {/* WALL (brick-orange) */}
        <mesh position={[0, yScene(Hf + (yWallTop - Hf) / 2), 0]}>
          <boxGeometry args={[L_s, (yWallTop - Hf) * scale, tw_s]} />
          <meshStandardMaterial color={COLOR_WALL} transparent opacity={0.68} roughness={1.0} />
        </mesh>
        <lineSegments position={[0, yScene(Hf + (yWallTop - Hf) / 2), 0]}>
          <edgesGeometry args={[new THREE.BoxGeometry(L_s, (yWallTop - Hf) * scale, tw_s)]} />
          <lineBasicMaterial color="#451A03" />
        </lineSegments>
        {/* Brick mortar lines */}
        {[0.2, 0.4, 0.6, 0.8].map((f, i) => (
          <mesh key={`m-${i}`}
            position={[0, yScene(Hf + (yWallTop - Hf) * f), 0]}>
            <boxGeometry args={[L_s * 1.001, 0.006, tw_s * 1.01]} />
            <meshBasicMaterial color="#451A03" />
          </mesh>
        ))}

        {/* MAIN BARS (transverse) */}
        {mainXs.map((x, i) => (
          <Cylinder key={`m-${i}`}
            radius={(d_main / 2) * scale} length={B_s}
            position={[x * scale, yScene(yMainBar), 0]}
            rotation={[Math.PI / 2, 0, 0]}
            color={COLOR_BAR_MAIN}
          />
        ))}
        {/* DISTRIBUTION BARS (longitudinal) */}
        {distZs.map((z, i) => (
          <Cylinder key={`d-${i}`}
            radius={(d_dist / 2) * scale} length={L_s}
            position={[0, yScene(yDistBar), z * scale]}
            rotation={[0, 0, Math.PI / 2]}
            color={COLOR_BAR_DIST}
          />
        ))}
      </Bounds>

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

function Cylinder({ radius, length, position, rotation, color }: {
  radius: number; length: number;
  position: [number, number, number]; rotation: [number, number, number];
  color: string;
}) {
  const geom = useMemo(() => new THREE.CylinderGeometry(radius, radius, length, 14), [radius, length]);
  return (
    <mesh position={position} rotation={rotation} geometry={geom}>
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}
